import React, { useEffect, useState } from 'react';
import {
  addSubmissionDataApi,
  getSubmissionsDataApi,
  getSubmissionsLongDataApi
} from '../api/submissions-database';
import Button from '../components/UI/Button';
import PlusIcon from '../components/icons/PlusIcon';
import SubmissionsTable from '../components/table/SubmissionsTable';
import { useSubmissionsContext } from '../context/submissions';
import { exportSubmissions } from '../utils/exportSubmissions';
import ReactGA from 'react-ga4';
import { toast } from 'react-hot-toast';
import { ActiveIcon, ArchiveIcon, ExportIcon } from '../components/icons/icons';
import GrayButton from '../components/submissions-database/GrayButton';
import DisplayPropertiesDropdown from '../components/submissions-database/dropdown/DisplayPropertiesDropdown';
import { useUserDetialsContext } from '../context/user-details';
import isProcessingTooLong from '../utils/isProcessingTooLong';
import parseDate from '../utils/parseDate';
import redirectToLogin from './../utils/token';
import DocPermissionEnum from 'enums/DocPermissionEnum';
import mergeArraysById from 'utils/mergeArraysById';
import SearchIcon from 'components/icons/SearchIcon';
import StyledSearchContainer from 'components/atoms/search/StyledSearchContainer';
import StyledSearchInput from 'components/atoms/search/StyledSearchInput';
import RenderFilters from 'components/submissions-database/RenderFilters';

const SubmissionsDatabase = () => {
  const {
    filters,
    submissionsData,
    setSubmissionData,
    setHiddenColumns,
    fetchMoreSubmissions,
    hasMore,
    setFilters,
    fetchSubmissions,
    loading,
    searchText,
    setSearchText,
    sortingCriteria,
    fetchAndSetFilterOption
  } = useSubmissionsContext();
  const [csvExportInProgress, setCsvExportInProgress] = useState(false);
  const [isLongPollingActive, setIsLongPollingActive] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const { userDetails } = useUserDetialsContext();

  const addSubmission = async () => {
    setInProgress(true);
    const newSubmission = {
      read: false,
      response_status: false,
      isArchived: false,
      priority: 'General',
      status: 'COMPLETED',
      type: 'SCRIPT',
      title: '',
      writer: [],
      director: [],
      producers: [],
      company_name: '',
      submitted_by: '',
      genre: [],
      logline: '',
      note: '',
      publisher: [],
      agency: '',
      actions: [
        DocPermissionEnum.DOC_OWN,
        DocPermissionEnum.DOC_VIEW,
        DocPermissionEnum.DOC_EDIT,
        DocPermissionEnum.DOC_DOWNLOAD
      ]
    };

    const presentFields = { ...newSubmission };
    delete presentFields.agency;
    delete presentFields.publisher;

    try {
      const response = await addSubmissionDataApi({ data: newSubmission });
      const newId = response.data.data._id;
      const createdSubmission = { ...newSubmission, _id: newId, created_at: new Date() };

      setSubmissionData((prevData) => {
        return [createdSubmission, ...prevData];
      });
    } catch (error) {
      console.error('Error adding submission:', error);
    } finally {
      setInProgress(false);
    }
  };

  const handleCSVExport = async () => {
    try {
      setCsvExportInProgress(true);
      const response = await getSubmissionsDataApi();
      const submissionList = response?.data?.data;

      if (!submissionList) {
        console.error('No data returned from API');
        return;
      }

      exportSubmissions(submissionList);
    } catch (error) {
      console.error('Error fetching submissions data:', error);
    } finally {
      setCsvExportInProgress(false);
    }
  };

  const submissionLongPolling = async () => {
    try {
      const isProcessing = submissionsData?.some((item) => {
        return item.status === 'PROCESSING' && !isProcessingTooLong(parseDate(item?.created_at));
      });
      if (!isProcessing) return;
      setIsLongPollingActive(true);
      const response = await getSubmissionsLongDataApi();
      const submissionList = response?.data?.data?.data || [];
      setSubmissionData((prev) => mergeArraysById(prev, submissionList));
    } catch (error) {
      console.log('error', error);
      if (error?.response?.status === 408) {
        submissionLongPolling();
      } else if (error?.response?.status === 401) {
        toast.error('Session expired. Please log in again to continue.');
        setTimeout(() => {
          redirectToLogin();
        }, 2000);
      }
    } finally {
      setIsLongPollingActive(false);
    }
  };

  useEffect(() => {
    fetchAndSetFilterOption();
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (!loading) fetchSubmissions();
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  useEffect(() => {
    if (!loading) fetchSubmissions();
  }, [filters, sortingCriteria]);

  useEffect(() => {
    if (submissionsData?.length && !isLongPollingActive) {
      submissionLongPolling();
    }
    const storedHiddenColumns = localStorage.getItem('hiddenColumns');
    if (storedHiddenColumns) {
      setHiddenColumns(JSON.parse(storedHiddenColumns));
    }
  }, [submissionsData]);

  useEffect(() => {
    if (userDetails && userDetails.oid) {
      try {
        ReactGA.event({
          category: 'Page Visit',
          action: 'Material Log Visit by OID',
          label: `${userDetails.oid}`
        });
      } catch (err) {
        console.error('Error setting user ID or sending pageview to GA', err);
      }
    }
  }, [userDetails?.oid]);

  return (
    <section className="z-[50] font-inter database-submissions-container submission-table-container">
      <div className="px-4 sm:px-3 md:px-4 lg:px-6 pb-4">
        <h2 className="text-lg font-inter">Material</h2>
        <div className="flex items-center w-full justify-start border-b border-gray-600 gap-2 py-2">
          <div className="flex gap-2">
            <GrayButton
              text="Active"
              icon={<ActiveIcon className="inline" />}
              onClick={() => setFilters((prev) => ({ ...prev, isArchived: false }))}
              className="px-2 md:px-2 py-1 text-xs"
              selected={!filters?.isArchived}
              isTableToggle
            />
            <GrayButton
              text="Archived"
              icon={<ArchiveIcon className="!h-4 !w-4" />}
              onClick={() => setFilters((prev) => ({ ...prev, isArchived: true }))}
              className="px-2 md:px-2 py-1 text-xs"
              selected={filters?.isArchived}
              isTableToggle
            />
          </div>
        </div>

        <div className="flex flex-col flex-wrap sm:flex-row items-start sm:items-center justify-between gap-3 mt-3">
          <div className="flex items-center gap-3">
            <RenderFilters />
            <StyledSearchContainer className="!border-slate-600 !bg-slate-900/40">
              <div className="flex p-2 space-x-1">
                <SearchIcon className="h-4 w-4" />
              </div>
              <StyledSearchInput
                placeholder="Search..."
                value={searchText}
                onChange={async (e) => setSearchText(e?.target?.value)}
                className="!text-sm"
              />
            </StyledSearchContainer>
          </div>
          <div className="flex items-center gap-1.5 md:gap-3">
            <GrayButton
              text="Add Row"
              icon={<PlusIcon />}
              onClick={addSubmission}
              loading={inProgress}
            />
            <DisplayPropertiesDropdown />
            <GrayButton
              text="Export Table"
              icon={<ExportIcon />}
              onClick={handleCSVExport}
              loading={csvExportInProgress}
            />
            <Button
              element={'link'}
              to="/coverage"
              className={
                'flex items-center gap-1.5 !px-2 md:!px-3.5 !py-1.5 !h-fit font-inter !rounded'
              }>
              <PlusIcon />
              <span>Generate Coverage</span>
            </Button>
          </div>
        </div>
      </div>
      <div>
        <div className={'p-0 rounded-[10px] w-full'}>
          <div className="relative h-full w-full">
            <SubmissionsTable
              hasMore={hasMore}
              fetchMoreSubmissions={fetchMoreSubmissions}
              submissionsData={submissionsData}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SubmissionsDatabase;
